import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { FiltersComponent } from './components/filters/filters.component';
import { ColumnsComponent } from './components/columns/columns.component';
import { SortingComponent } from './components/sorting/sorting.component';
import { UtilsTabsService } from '../../services/utils-tabs.service';
import { MatTooltip } from '@angular/material/tooltip';
import { TableType } from '../../../../models/shipment';
import { catchError, filter, firstValueFrom, NEVER, Subscription, switchMap, tap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Tab } from '../../models/utils-tabs';
import { openConfirmationDialog } from '../../../confirmation-popup/confirmation-popup';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { ErrorHandlerService } from '../../../../utils/cross-xss-script-error-handler-service';
import { ColumnNameByKeyPipe } from './pipes/column-name-by-key.pipe';

interface DialogData {
  type?: TableType;
  order?: number;
  tab?: Tab;
}

@Component({
  selector: 'app-tabs-configurator',
  standalone: true,
  templateUrl: './tabs-configurator.component.html',
  imports: [
    ReactiveFormsModule,
    MatInput,
    MatFormField,
    MatDialogContent,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    FiltersComponent,
    ColumnsComponent,
    SortingComponent,
    MatError,
    MatDialogTitle,
    MatTooltip,
    MatProgressSpinner,
    MatIcon
  ],
  styleUrl: './tabs-configurator.component.scss',
  providers: [ColumnNameByKeyPipe]
})
export class TabsConfiguratorComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  saving = false;
  deleting = false;

  private subscription = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private utilsTabsService: UtilsTabsService,
    private matSnackBar: MatSnackBar,
    private matDialogRef: MatDialogRef<TabsConfiguratorComponent>,
    private matDialog: MatDialog,
    private errorHandler: ErrorHandlerService,
    private columnNameByKeyPipe: ColumnNameByKeyPipe
  ) {}

  ngOnInit() {
    this.createEmptyForm();
    if (this.data.tab) {
      this.updateFormWithTabData();
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  getFiltersArray() {
    return this.form.get('filters') as FormArray;
  }

  getColumnsArray() {
    return this.form.get('columns') as FormArray;
  }

  getSortingArray() {
    return this.form.get('sorts') as FormArray;
  }

  save() {
    if (this.shouldDisableSave()) {
      return;
    }
    this.saving = true;
    const rawValue = this.form.getRawValue();
    const isUpdate = !!this.data.tab;
    const successMessage = isUpdate ? 'Tab has been updated successfully' : 'New tab has been created successfully';
    const errorMessage = isUpdate
      ? 'Could not update the tab. Please try again later.'
      : 'Could not create a new tab. Please try again later.';

    const saveOperation = isUpdate
      ? this.utilsTabsService.updateTab(this.data.tab!.id, rawValue)
      : this.utilsTabsService.createTab(rawValue);

    this.subscription.add(
      saveOperation
        .pipe(
          tap(() => {
            this.saving = false;
            this.matSnackBar.open(successMessage, 'x', { panelClass: 'snackbar-green' });
            this.matDialogRef.close(isUpdate ? 'Updated' : 'Created');
          }),
          catchError(async error => {
            this.saving = false;
            console.error(error);

            const filters = (this.form.value.filters ?? []) as {
              parameters: string;
              order: string;
              columnKey: string;
            }[];

            const allAvailableColumns: { key: string; name: string }[] = [];
            for (const filter of filters) {
              for (const parameter of Object.keys(filter.parameters ?? {})) {
                allAvailableColumns.push({
                  key: `filters[${filter.order}].parameters.${parameter}`,
                  name: await firstValueFrom(
                    this.columnNameByKeyPipe.transform(
                      filter.columnKey,
                      this.data.type ?? this.data.tab?.type ?? 'Shipments'
                    )
                  )
                });
              }
            }
            allAvailableColumns.push({
              key: 'name',
              name: 'Tab name'
            });
            this.errorHandler.showApiError(
              error,
              errorMessage,
              allAvailableColumns,
              (fieldName, options) => options.find(x => x.key === fieldName)?.name
            );
            return NEVER;
          })
        )
        .subscribe()
    );
  }

  deleteTab() {
    this.subscription.add(
      openConfirmationDialog(this.matDialog, {
        title: 'Delete tab',
        message: 'Are you sure you want to delete this tab?'
      })
        .afterClosed()
        .pipe(
          filter(result => result === 'Confirmed'),
          tap(() => (this.deleting = true)),
          switchMap(() => this.utilsTabsService.deleteTab(this.data.tab!.id)),
          catchError(error => {
            console.error(error);
            this.matSnackBar.open('The tab could not be deleted. Please try again later.', 'x');
            this.deleting = false;
            return NEVER;
          }),
          tap(() => {
            this.deleting = false;
            this.matSnackBar.open('The tab has been deleted', 'x', {
              panelClass: 'snackbar-green'
            });
            this.matDialogRef.close('Deleted');
          })
        )
        .subscribe()
    );
  }

  shouldDisableSave() {
    return this.form.invalid || this.getColumnsArray().length === 0;
  }

  getType() {
    return this.data.type || this.data.tab?.type || 'Shipments';
  }

  private updateFormWithTabData() {
    const { name, order, type } = this.data.tab!;
    this.form.get('name')?.patchValue(name);
    this.form.get('order')?.patchValue(order);
    this.form.get('type')?.patchValue(type);
  }

  private createEmptyForm() {
    this.form = new FormGroup({
      name: new FormControl(null, Validators.required),
      order: new FormControl(this.data.order),
      type: new FormControl(this.data.type),
      filters: new FormArray([]),
      columns: new FormArray([]),
      sorts: new FormArray([])
    });
  }
}
